import {
    Component,
    ElementRef,
    OnDestroy,
    OnInit,
    QueryList,
    TemplateRef,
    ViewChild,
    ViewChildren,
} from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { TranslateService } from '@ngx-translate/core';
import { Logger, ModalDialogNotificationService, NotificationService, NotificationType } from '@se/se.ui.html5';
import { BehaviorSubject, Subscription } from 'rxjs';
import { ConfirmDialogService } from '../confirm-dialog.service';
import { EstensaUser } from '../../models/estensa-models.model';
import { AuthenticationService } from '../../services/authentication/authentication.service';
import { AuthData } from '../auth-data';
import { IResponseStatus } from '../../services/report-workflow-exam.service';
import { MatTabChangeEvent } from '@angular/material/tabs';

@Component({
    selector: 'se-multi-confirm-dialog',
    templateUrl: './multi-confirm-dialog.component.html',
    styleUrls: ['./multi-confirm-dialog.component.scss'],
})
export class MultiConfirmDialogComponent implements OnDestroy, OnInit {
    @ViewChild('confirmDialogTemplate')
    confirmDialogTemplate!: TemplateRef<any>;

    @ViewChildren('pwd') pwdElements!: QueryList<ElementRef>;

    confirmLabel: string = '';
    nextLabel: string = '';

    get isWaiting(): BehaviorSubject<boolean> {
        return this.confirmDialogService.isWaiting;
    }

    confirmDialog!: MatDialogRef<any>;

    isOnError: boolean = false;
    errorMessages: string[] = [];
    errorMessage: string = '';
    // data members
    data: AuthData[] = [];
    showPassword: boolean[] = [];
    selectedTabIndex: number = 0;

    private afterCloseEventSubscription!: Subscription;
    private notifyFailureSubscription: Subscription;
    private notifySuccessSubscription: Subscription;
    private openSubscription: Subscription;

    // constructor

    constructor(
        public dialog: MatDialog,
        private confirmDialogService: ConfirmDialogService,
        private logger: Logger,
        private translateService: TranslateService,
        private notificationService: NotificationService,
        private authenticationService: AuthenticationService,
        private modalNotificationService: ModalDialogNotificationService,
    ) {
        this.confirmLabel = this.translateService.instant('Confirm');
        this.nextLabel = this.translateService.instant('Next');

        this.openSubscription = this.confirmDialogService.openMultiConfirmWindowRequired.subscribe(
            async (userIds: string[]) => {
                try {
                    this.data = await this.getMultiAuthData(userIds);
                    this.showPassword = [...new Array(this.data.length)].fill(false);
                    this.showDialog();
                } catch (err) {
                    this.logger.error(err);
                }
            },
        );

        this.notifySuccessSubscription = this.confirmDialogService.notifySuccess.subscribe((result) => {
            this.confirmDialog?.close(true);

            if (!result) {
                this.notificationService.success(
                    this.translateService.instant(this.confirmDialogService.successMessageKey),
                );
                this.modalNotificationService.notify({
                    message: this.translateService.instant(this.confirmDialogService.successMessageKey),
                    title: this.translateService.instant('Information'),
                    type: NotificationType.information,
                    buttonYes: false,
                    buttonNo: false,
                    buttonOk: true,
                    buttonCancel: false,
                });
            } else if (typeof result === 'object') {
                this.modalNotificationService.notify({
                    message: this.translateService.instant(this.confirmDialogService.partialMessageKey, {
                        p0: result.operationFailedCount,
                        p1: result.operationCount,
                    }),
                    title: this.translateService.instant('Warning'),
                    type: NotificationType.warning,
                    buttonYes: false,
                    buttonNo: false,
                    buttonOk: true,
                    buttonCancel: false,
                });
            }
        });

        this.notifyFailureSubscription = this.confirmDialogService.notifyMultiFailure.subscribe((responses) => {
            this.isOnError = true;
            this.handleMultiFailure(responses);
        });
    }

    /**
     * resetta le password in errore in base alla lista delle risposte del server
     * inserisce i corrispondenti error messages
     * attiva il primo tab in errore. di conseguenza il listener corrispondente mette in focus la password
     * @param responses nel caso multi sara sempre solo una risposta perche si tratta di un unico esame con molti firmatari
     * verra segnalato solo un'errore di credenziali per richiesta che si trova nella prop AdditionalInfo
     */
    handleMultiFailure = (responses: IResponseStatus[]) => {
        //resettare solo le password con errore
        //codice di test che mette in errore piu passwords
        // responses.forEach((response, idx) => {
        //     if (response.responseStatusType === SignResultStatus.Failed) {
        //         this.data[idx].password = '';
        //         this.errorMessages[idx] = this.translateService.instant(response.error.message);
        //     } else {
        //         this.errorMessages[idx] = '';
        //     }
        // });
        // this.selectedTabIndex = responses.findIndex((response) => response.responseStatusType === SignResultStatus.Failed);
        const errorTitle = this.translateService.instant(this.confirmDialogService.errorMessageKey, { p0: '' });
        this.notificationService.error(errorTitle); // Aggiungo il dettaglio al messaggio generico nella notifica
        const response = responses[0];
        if (response.additionalInfo) {
            //this.errorMessage = this.translateService.instant(this.confirmDialogService.errorMessageKey);
            const username = response.additionalInfo.username;
            const domain = response.additionalInfo.domain;
            this.selectedTabIndex = this.data.findIndex(
                (userData) => userData.userName === username && userData.domain === domain,
            );
            this.data[this.selectedTabIndex].password = '';
            this.errorMessages[this.selectedTabIndex] = this.translateService.instant(response.error.message);
            this.data.forEach((userData, idx) => {
                if (userData.userName !== username || userData.domain !== domain) {
                    this.errorMessages[idx] = '';
                }
            });
        } else if (response.error) {
            this.errorMessages = [];
            this.errorMessage = this.translateService.instant(response.error.message);
            this.notificationService.error(this.errorMessage);
            if (response.error.message === 'Exception_CredentialsFault') {
                //non specifica chi ha sbagliato rimuovere tutte le pwd
                this.data.forEach((d) => (d.password = ''));
                this.selectedTabIndex = 0;
            }
        }
    };

    getMultiAuthData = async (userIds: string[]): Promise<AuthData[]> => {
        if (!userIds || !Array.isArray(userIds)) {
            return Promise.reject(new Error('userIds is not defined'));
        }

        const estensaUsers = await Promise.all(
            userIds.map((userId: string) => this.authenticationService.getUserInfo(userId)),
        );

        return estensaUsers.map(({ userName, domain }: EstensaUser) => ({ userName, domain, password: '' }));
    };

    onSelectedTabIndexChange = (changeEvent: MatTabChangeEvent) => {
        const pwdElement = this.pwdElements.get(changeEvent.index);
        if (pwdElement && !this.data[changeEvent.index].password)
            setTimeout((_) => pwdElement.nativeElement.focus(), 250);
    };

    ngOnInit() {
        this.safeExit();
    }

    ngOnDestroy() {
        this.notifyFailureSubscription.unsubscribe();
        this.notifySuccessSubscription.unsubscribe();
        this.openSubscription.unsubscribe();

        if (this.afterCloseEventSubscription) {
            this.afterCloseEventSubscription.unsubscribe();
        }
    }

    setNextTabToSign() {
        this.selectedTabIndex = this.data.findIndex((signer) => !signer.password);
    }

    currentIsSet(): boolean {
        return Boolean(this.data[this.selectedTabIndex].password);
    }

    handleConfirmClick() {
        if (this.enableAuthenticateButton()) return this.closeModal(false);
        this.setNextTabToSign();
    }

    public closeModal(cancelOps: boolean) {
        this.isOnError = false;
        if (!cancelOps) {
            // Richiedo operazione
            this.confirmDialogService.endMultiConfirmFlow(this.data);
        } else {
            //@TODO i corrispondenti cancel vanno implementati con versione multi???
            // Chiudo
            this.confirmDialogService.cancelConfirmFlow();
            this.confirmDialog.close(true);
        }
    }

    public enableAuthenticateButton = () =>
        this.data.reduce((acc, credentials) => acc && Boolean(credentials.userName && credentials.password), true);

    // private functions
    private showDialog = () => {
        this.confirmDialog = this.dialog.open(this.confirmDialogTemplate, {
            autoFocus: true,
            height: '45%',
            width: '30%',
            minHeight: '355px',
            minWidth: '480px',
            disableClose: true,
            panelClass: 'outer-sign-dialog-wrapper',
        });

        this.afterCloseEventSubscription = this.confirmDialog.afterClosed().subscribe((wasClosedAutomatically) => {
            // Alla chiusura della dialog faccio le pulizie del caso
            if (!wasClosedAutomatically) {
                this.confirmDialogService.cancelConfirmFlow();
            }
            this.safeExit();
            this.confirmDialogService.safeExit();
        });
    };

    private safeExit = () => {
        this.isOnError = false;
        this.errorMessages = [];
        this.data = [];

        if (this.afterCloseEventSubscription) {
            this.afterCloseEventSubscription.unsubscribe();
        }
    };
}
